import { useParams, Link, useNavigate } from "react-router-dom";
import { usePageBar } from "../contexts/PageBarContext";
import { useTripName } from "../hooks/useTripName";
import { routeAirportIds } from "../utils/flightRoute";
import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useInteractions } from "../hooks/useInteractions";
import { useSegments, updateSegment, deleteSegment, createSegment, commitItineraryReconciliation } from "../hooks/useSegments";
import { useTripZone } from "../hooks/useTripZone";
import { useTravelParty } from "../hooks/useTravelParty";
import { useEntityMap } from "../hooks/useEntityMap";
import {
  SegmentForm,
  type SegmentFormEdits,
  type SegmentFormResult,
} from "../components/segments/SegmentForm";
import { LogActivityPicker } from "../components/interactions/LogActivityPicker";
import { LogActivitySheet } from "../components/interactions/LogActivitySheet";
import { AddDropdown } from "../components/trips/AddDropdown";
import { getRoleLabel, SEGMENT_ROLE_TYPES } from "../config/peopleRoleTypes";
import { getSegmentAndDescendantIds } from "../utils/segmentTree";
import {
  segmentDayLookup, segmentZoneLookup, segmentLogSeeds, formatDuration } from "../utils/tripDisplay";
import { ActivityList } from "../components/itinerary/ActivityList";
import { SegmentBlock } from "../components/itinerary/SegmentBlock";
import { InteractionsAnalytics } from "../components/itinerary/InteractionsAnalytics";
import { NotesDisplay } from "../components/common/NotesDisplay";
import { WhoWasThereList } from "../components/common/WhoWasThere";
import { segmentParticipantsLookup } from "../utils/participants";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import { changedFields } from "../utils/editDiff";
import {
  dayTypeIcon,
  dayTypeLabel,
  generateItineraryDays,
  reconcileItinerary,
  type ExistingItineraryDay,
} from "../utils/cruiseItinerary";
import { getSegmentType, isCruiseDay, isCruiseSegment, isTravelSegment } from "../types";
import { travelDaySummary } from "../utils/travelDay";
import type { Segment, Entity } from "../types";
import { dayKeyOf, daysBetweenKeys, formatDayKey } from "../utils/calendarDay";

type AddMode = null | "segment" | "activity";

/** The segment fields an edit can change, other than its dates. */
type SegmentEdit = Omit<SegmentFormResult, "startDate" | "endDate" | "notes"> & { notes: string | null };

export function SegmentDetail() {
  const { tripId, segmentId } = useParams<{ tripId: string; segmentId: string }>();
  const navigate = useNavigate();
  const [segment, setSegment] = useState<Segment | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  // The segment as the edit form opened with it — what a save diffs against.
  const [openedSegment, setOpenedSegment] = useState<Segment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [generatingItinerary, setGeneratingItinerary] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [activityDefaultSegmentId, setActivityDefaultSegmentId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState<string | null>(null);
  const [expandedSubSegments, setExpandedSubSegments] = useState<Set<string>>(new Set());

  const { segments: allSegments, loading: segmentsLoading } = useSegments(tripId);
  const tripZone = useTripZone(tripId);
  const travelParty = useTravelParty(tripId);
  const childSegments = allSegments.filter((s) => s.parentSegmentId === segmentId);

  const { interactions: allInteractions, loading: interactionsLoading } = useInteractions(tripId);
  const segmentInteractions = allInteractions.filter((i) => i.segmentId === segmentId);

  const interactionEntityIds = allInteractions.map((i) => i.entityId).filter((id): id is string => id != null);
  const segmentEntityIds = allSegments.map((s) => s.entityId).filter((id): id is string => id != null);
  const cruiseEntityIds = [segment?.cruiseConfig?.shipEntityId, segment?.cruiseConfig?.cruiseLineEntityId]
    .filter((id): id is string => id != null);
  const allEntityIds = [
    ...new Set([...interactionEntityIds, ...segmentEntityIds, ...cruiseEntityIds, ...routeAirportIds(allInteractions)]),
  ];
  const { entityMap, entityDataMap, loading: entitiesLoading } = useEntityMap(allEntityIds);

  const seedEntityIds = useMemo(
    () => segmentLogSeeds(allSegments, allInteractions, activityDefaultSegmentId ?? segmentId ?? null),
    [allSegments, allInteractions, activityDefaultSegmentId, segmentId]
  );

  useEffect(() => {
    if (!tripId || !segmentId) return;
    // Resilient: reached straight after `createSegment` queues the write, so
    // the segment may not be on the server yet. See `resilientSnapshot.ts`.
    const unsub = onSnapshotResilient(
      doc(db, "trips", tripId, "segments", segmentId),
      (snapshot) => {
        if (snapshot.exists()) {
          setSegment(snapshot.data() as Segment);
        }
        setLoading(false);
      },
      {
        label: `segment ${segmentId}`,
        onError: ({ error, hadPendingWrites }) => {
          setLoading(false);
          // Same as the trip page: don't keep rendering cached data the server
          // refuses, unless it's our own write still on its way up.
          if (error.code === "permission-denied" && !hadPendingWrites) setSegment(null);
        },
      },
    );
    return unsub;
  }, [tripId, segmentId]);

  // The top bar: up to the parent segment (a cruise, for its days) or the trip.
  const tripName = useTripName(tripId);
  const parentSegment = segment?.parentSegmentId
    ? allSegments.find((s) => s.id === segment.parentSegmentId) ?? null
    : null;
  usePageBar({
    title: segment
      ? isCruiseDay(segment) && typeof segment.dayNumber === "number"
        ? `Day ${segment.dayNumber}`
        : segment.name
      : null,
    parent: segment?.parentSegmentId
      ? { to: `/trips/${tripId}/segments/${segment.parentSegmentId}`, label: parentSegment?.name ?? "Back" }
      : { to: `/trips/${tripId}`, label: tripName ?? "Trip" },
    cancel: editing ? { onClick: () => { setEditing(false); setOpenedSegment(null); } } : null,
  });

  useEffect(() => {
    if (!segment?.entityId) {
      setEntityName(null);
      return;
    }
    getDoc(doc(db, "entities", segment.entityId)).then((snap) => {
      if (snap.exists()) {
        setEntityName((snap.data() as Entity).displayName);
      }
    }).catch(() => setEntityName(null));
  }, [segment?.entityId]);

  if (loading) return <div className="loading">Loading segment...</div>;
  if (!segment || !tripId || !segmentId) {
    return <p>Segment not found. <Link to={`/trips/${tripId}`}>Back to trip</Link></p>;
  }

  const isDay = isCruiseDay(segment);
  const isCruise = isCruiseSegment(segment);
  const isTravel = !isDay && isTravelSegment(segment);
  const travel = isTravel ? travelDaySummary(segmentInteractions, (id) => entityDataMap[id]) : null;
  const travelSummary = travel
    ? [
        travel.route,
        travel.doorToDoorMs != null ? `${formatDuration(travel.doorToDoorMs / 1000)} door to door` : null,
        travel.flights > 0 ? `${travel.flights} flight${travel.flights === 1 ? "" : "s"}` : null,
      ].filter(Boolean).join(" · ")
    : "";
  const cfg = segment.cruiseConfig;

  // Who was on this segment — its own group, or the one it inherits. Read-only
  // here (it's edited in the segment form), and only worth a section when it's
  // narrower than the whole travel party.
  const partyIds = Object.keys(travelParty);
  const whoWasThere = segmentParticipantsLookup(allSegments, partyIds)(segmentId);
  const showWhoWasThere = partyIds.length > 0 && whoWasThere.length < partyIds.length;

  const dayCards = isCruise
    ? childSegments.filter((s) => isCruiseDay(s)).sort((a, b) => (a.dayNumber ?? 0) - (b.dayNumber ?? 0))
    : [];
  const otherChildSegments = isCruise ? childSegments.filter((s) => !isCruiseDay(s)) : childSegments;

  const cruiseSummary = (() => {
    if (!isCruise) return null;
    const shipName = cfg?.shipEntityId ? entityMap[cfg.shipEntityId] : null;
    const lineName = cfg?.cruiseLineEntityId ? entityMap[cfg.cruiseLineEntityId] : null;
    const parts: string[] = [];
    if (shipName) parts.push(shipName);
    else if (lineName) parts.push(lineName);
    if (cfg?.nights != null) parts.push(`${cfg.nights} night${cfg.nights === 1 ? "" : "s"}`);
    if (cfg?.homePort) parts.push(`from ${cfg.homePort}`);
    return parts.length > 0 ? parts.join(" · ") : null;
  })();

  const toggleSubSegment = (id: string) => {
    setExpandedSubSegments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openLogActivity = (forSegmentId: string | null) => {
    setActivityDefaultSegmentId(forSegmentId);
    setAddMode("activity");
  };
  const closeLogActivity = () => {
    setAddMode(null);
    setActivityDefaultSegmentId(null);
  };

  const formatDateRange = () => {
    // Calendar days: no zone moves them.
    const start = dayKeyOf(segment.startDate);
    const end = dayKeyOf(segment.endDate);
    if (!start || !end) return "";
    if (isDay) {
      return formatDayKey(start, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    }
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
    return `${formatDayKey(start, opts)} — ${formatDayKey(end, opts)}`;
  };

  const backLink = segment.parentSegmentId
    ? `/trips/${tripId}/segments/${segment.parentSegmentId}`
    : `/trips/${tripId}`;

  const handleEditSubmit = async (data: SegmentFormResult, edits: SegmentFormEdits) => {
    // Write only what this edit changed, measured against the segment as the
    // form opened with it — not against the live one, and not every field the
    // form holds. Otherwise a form left open (or saved offline) writes back
    // its opening copy of everything, undoing whatever anyone else changed on
    // this segment meanwhile. `before` is shaped the way the form shapes its
    // result, so an untouched field compares equal.
    const base = openedSegment ?? segment;
    const before: SegmentEdit = {
      name: base.name ?? "",
      parentSegmentId: base.parentSegmentId ?? null,
      entityId: base.entityId ?? null,
      notes: base.notes || null,
      participants: base.participants && base.participants.length > 0 ? base.participants : null,
      people: (base.people ?? []).filter((p) => p.name.trim()),
      segmentType: isDay ? "general" : getSegmentType(base),
      cruiseConfig: base.cruiseConfig ?? null,
      dayType: base.dayType ?? null,
      portName: base.portName ?? null,
      portEntityId: base.portEntityId ?? null,
    };
    const after: SegmentEdit = {
      name: data.name,
      parentSegmentId: data.parentSegmentId,
      entityId: data.entityId,
      notes: data.notes || null,
      participants: data.participants,
      people: data.people,
      segmentType: data.segmentType,
      cruiseConfig: data.cruiseConfig,
      dayType: data.dayType,
      portName: data.portName,
      portEntityId: data.portEntityId,
    };
    const patch: Parameters<typeof updateSegment>[2] = changedFields(before, after);
    if (edits.datesChanged) {
      patch.startDate = data.startDate;
      patch.endDate = data.endDate;
    }
    if (Object.keys(patch).length > 0) await updateSegment(tripId, segmentId, patch);
    setEditing(false);
    setOpenedSegment(null);
  };

  const openEdit = () => {
    setOpenedSegment(segment);
    setEditing(true);
  };

  const handleDelete = async () => {
    const msg = isCruise
      ? "Delete this cruise segment, its itinerary days, and the activities under them? This cannot be undone."
      : "Delete this segment? Its activities, and any segments inside it, move up a level.";
    if (!confirm(msg)) return;
    setDeleting(true);
    await deleteSegment(tripId, segmentId);
    navigate(backLink);
  };

  const handleAddSubSegment = async (data: SegmentFormResult) => {
    const newId = await createSegment(tripId, data);
    setAddMode(null);
    navigate(`/trips/${tripId}/segments/${newId}`);
  };

  const handleGenerateItinerary = async () => {
    if (generatingItinerary) return;
    const start = segment.startDate?.toDate();
    if (!start) return;
    const startKey = dayKeyOf(segment.startDate);
    const endKey = dayKeyOf(segment.endDate) ?? startKey;
    const fallbackNights = Math.max(1, startKey && endKey ? daysBetweenKeys(startKey, endKey) : 1);
    const nights = cfg?.nights ?? fallbackNights;
    const drafts = generateItineraryDays(start, nights);
    const existing: ExistingItineraryDay[] = dayCards.map((s) => ({
      id: s.id,
      dayNumber: s.dayNumber,
      dayType: s.dayType,
      portName: s.portName,
      notes: s.notes,
      name: s.name,
      hasActivities: allInteractions.some((i) => i.segmentId === s.id),
    }));
    const plan = reconcileItinerary(existing, drafts);
    if (plan.toRemove.length > 0) {
      const labels = plan.toRemove
        .map((r) => (typeof r.dayNumber === "number" ? `Day ${r.dayNumber}` : "an extra day"))
        .join(", ");
      const n = plan.toRemove.length;
      if (!confirm(
        `Regenerating will remove ${n} day${n > 1 ? "s" : ""} (${labels}) that have no port, notes, or activities. Continue?`,
      )) return;
    }
    setGeneratingItinerary(true);
    try {
      await commitItineraryReconciliation(tripId, segmentId, plan);
    } finally {
      setGeneratingItinerary(false);
    }
  };

  if (editing) {
    return (
      <div className="page">
        <h1>{isDay ? "Edit Day" : isCruise ? "Edit Cruise" : "Edit Segment"}</h1>
        <SegmentForm
          initial={segment}
          onSubmit={handleEditSubmit}
          onCancel={() => { setEditing(false); setOpenedSegment(null); }}
          submitLabel="Save Changes"
          segments={allSegments}
          excludeSegmentIds={getSegmentAndDescendantIds(allSegments, segmentId)}
          dayMode={isDay}
          travelParty={travelParty}
        />
      </div>
    );
  }

  const dropdownItems = [
    { label: "Add Sub-Segment", onClick: () => setAddMode("segment") },
    { label: "Log Activity", onClick: () => openLogActivity(segmentId) },
  ];

  const itinerarySectionTitle = isCruise ? "Itinerary" : childSegments.length > 0 ? "Schedule" : "Activities";

  return (
    <div className="page">
      <div className="detail-header">
        <div>
          <h1>
            {(isDay || isCruise || isTravel) && (
              <span className="segment-day-icon" aria-hidden="true">{isCruise ? "🚢" : isTravel ? "🧳" : dayTypeIcon(segment.dayType)} </span>
            )}
            {isDay && typeof segment.dayNumber === "number" ? `Day ${segment.dayNumber}` : segment.name}
            {isDay && segment.portName?.trim() ? ` — ${segment.portName.trim()}` : ""}
          </h1>
          <p className="detail-date">{formatDateRange()}</p>
          {cruiseSummary && <p className="detail-subtitle">{cruiseSummary}</p>}
          {travelSummary && <p className="detail-subtitle">{travelSummary}</p>}
          {isDay && segment.dayType && <span className="status-badge">{dayTypeLabel(segment.dayType)}</span>}
          {!isDay && !isCruise && entityName && <span className="status-badge">{entityName}</span>}
        </div>
        <div className="detail-actions">
          <button className="btn btn-small btn-secondary" onClick={openEdit}>Edit</button>
          <button className="btn btn-small btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>

      {segment.notes && <NotesDisplay value={segment.notes} className="detail-notes" />}

      {addMode === "segment" && (
        <div className="add-form-wrapper">
          <SegmentForm
            onSubmit={handleAddSubSegment}
            onCancel={() => setAddMode(null)}
            segments={allSegments}
            defaultParentSegmentId={segmentId}
            travelParty={travelParty}
          />
        </div>
      )}

      <LogActivitySheet open={addMode === "activity"} onClose={closeLogActivity}>
        <LogActivityPicker
          tripId={tripId}
          segments={allSegments}
          defaultSegmentId={activityDefaultSegmentId ?? segmentId}
          seedEntityIds={seedEntityIds}
          interactions={allInteractions}
        />
      </LogActivitySheet>

      {showWhoWasThere && (
        <section>
          <h2>Who was there</h2>
          <WhoWasThereList travelParty={travelParty} memberIds={whoWasThere} />
          <p className="who-here-hint">
            {segment.participants?.length
              ? "Activities logged here start with these people."
              : `Inherited from the parent ${isDay ? "cruise" : "segment"}. Set a group of this ${isDay ? "day" : "segment"}'s own from Edit.`}
          </p>
        </section>
      )}

      {segment.people.length > 0 && (
        <section>
          <h2>{isCruise ? "Crew / Staff" : "People"}</h2>
          <div className="people-list">
            {segment.people.map((p, i) => (
              <div key={i} className="people-item">
                <span className="people-role">{getRoleLabel(p.role, SEGMENT_ROLE_TYPES)}</span>
                <span className="people-name">{p.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="section-header">
          <h2 className="itinerary-title">{itinerarySectionTitle}</h2>
          <div className="section-header-actions">
            {isCruise && (
              <button
                className="btn btn-small btn-secondary"
                onClick={handleGenerateItinerary}
                disabled={generatingItinerary || segmentsLoading || interactionsLoading}
              >
                {generatingItinerary ? "Working…" : dayCards.length > 0 ? "Regenerate" : "Generate itinerary"}
              </button>
            )}
            <AddDropdown items={dropdownItems} />
          </div>
        </div>

        {segmentsLoading || interactionsLoading || entitiesLoading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="itinerary">
            {isCruise && dayCards.length === 0 && (
              <div className="itinerary-empty">
                <p className="text-muted">
                  No itinerary yet. Click <strong>Generate itinerary</strong> to build day-by-day cards from the sailing dates.
                </p>
              </div>
            )}

            {dayCards.map((child) => (
              <SegmentBlock
                key={child.id}
                segment={child}
                allSegments={allSegments}
                tripZone={tripZone}
                interactions={allInteractions}
                entityMap={entityMap}
                entityDataMap={entityDataMap}
                tripId={tripId}
                expandedSegments={expandedSubSegments}
                toggleSegment={toggleSubSegment}
                onAddActivity={openLogActivity}
              />
            ))}

            {otherChildSegments.map((child) => (
              <SegmentBlock
                key={child.id}
                segment={child}
                allSegments={allSegments}
                tripZone={tripZone}
                interactions={allInteractions}
                entityMap={entityMap}
                entityDataMap={entityDataMap}
                tripId={tripId}
                expandedSegments={expandedSubSegments}
                toggleSegment={toggleSubSegment}
                onAddActivity={openLogActivity}
              />
            ))}

            {segmentInteractions.length > 0 && (
              <ActivityList
                activities={segmentInteractions}
                entityMap={entityMap}
                entityDataMap={entityDataMap}
                tripId={tripId}
                onDay={segmentDayLookup(allSegments)}
                onZone={segmentZoneLookup(allSegments, tripZone)}
              />
            )}

            {!isCruise && childSegments.length === 0 && segmentInteractions.length === 0 && !entitiesLoading && (
              <div className="itinerary-empty">
                <p className="text-muted">No activities yet. Use the <strong>+</strong> button to add sub-segments or log activities.</p>
              </div>
            )}
          </div>
        )}
      </section>

      {segmentInteractions.length > 0 && !interactionsLoading && !entitiesLoading && (
        <section>
          <h2>Analytics</h2>
          <InteractionsAnalytics
            interactions={segmentInteractions}
            entityDataMap={entityDataMap}
          />
        </section>
      )}
    </div>
  );
}
